import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useEffect, useRef, useState } from "react"
import { CornerTicks } from "@/components/ui/corner-ticks"
import { EncryptedText } from "@/components/ui/encrypted-text"
import { MutuPrompt } from "@/components/ui/mutu-prompt"
import { SectionTitle } from "@/components/ui/section-title"
import { SubjectCaption } from "@/components/ui/subject-caption"
import { TransmissionPanel } from "@/components/ui/transmission-panel"
import { CAST, type Cast } from "@/data/cast"
import { cn } from "@/lib/utils"

gsap.registerPlugin(ScrollTrigger, useGSAP)

const ROTATE_INTERVAL_MS = 6500

export function CastSection() {
  const [active, setActive] = useState(0)
  const subject = CAST[active]

  const sectionRef = useRef<HTMLElement>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)
  const sweepRef = useRef<HTMLDivElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: necessary to retrigger narration animation on subject change
  useEffect(() => {
    const id = window.setTimeout(() => {
      setActive((i) => (i + 1) % CAST.length)
    }, ROTATE_INTERVAL_MS)
    return () => window.clearTimeout(id)
  }, [active])

  useGSAP(
    () => {
      if (!sectionRef.current) return

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return
      }

      gsap.set(bubbleRef.current, {
        opacity: 0,
        y: 24,
        scale: 0.985,
        transformOrigin: "top left",
      })
      gsap.set(sweepRef.current, { xPercent: -130, opacity: 0 })

      const tl = gsap.timeline({ paused: true })

      tl.to(bubbleRef.current, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.7,
        ease: "power3.out",
      })
        .to(
          sweepRef.current,
          { xPercent: 130, opacity: 0.7, duration: 0.7, ease: "power2.inOut" },
          "<0.05"
        )
        .to(sweepRef.current, { opacity: 0, duration: 0.2 }, ">-0.1")

      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top 75%",
        once: true,
        onEnter: () => tl.play(),
      })

      const mm = gsap.matchMedia()
      mm.add("(min-width: 768px)", () => {
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: "top top",
          end: "+=140%",
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        })
      })
    },
    { scope: sectionRef }
  )

  return (
    <section
      ref={sectionRef}
      id="cast"
      className="relative z-10 flex min-h-svh w-full flex-col overflow-x-hidden px-5 py-20 sm:px-10 sm:py-28 md:z-auto lg:py-32"
    >
      <div className="relative z-30 mb-8 flex justify-center md:hidden">
        <SectionTitle prefix="The" accent="Casts" />
      </div>
      <div className="grid w-full grid-cols-1 gap-10 sm:gap-12 md:grid-cols-2">
        <div className="relative z-30 order-2 flex flex-col md:order-1">
          <div className="mx-auto max-w-xl md:mr-0 md:ml-auto md:translate-x-16">
            <div className="hidden justify-center md:flex">
              <SectionTitle prefix="The" accent="Casts" />
            </div>
            <div ref={bubbleRef} className="relative md:mt-10">
              <TransmissionPanel className="relative overflow-hidden">
                <div
                  ref={sweepRef}
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 left-0 w-[45%] bg-linear-to-r from-transparent via-brand/20 to-transparent"
                />
                <NarrationContent subject={subject} />
              </TransmissionPanel>
              <span
                aria-hidden="true"
                className="absolute bottom-0 left-12 hidden size-4 translate-y-1/2 rotate-45 border-white/15 border-r border-b bg-background/45 backdrop-blur-sm md:block"
              />
            </div>
          </div>
          <div
            className="pointer-events-none hidden grow md:block md:min-h-[40vh]"
            aria-hidden="true"
          />
        </div>
        <div className="relative z-30 order-1 flex flex-col items-center justify-end md:order-2 md:-translate-x-8 md:items-end md:pt-12 lg:-translate-x-16 lg:pt-16">
          <Carousel active={active} onChange={setActive} subject={subject} />
        </div>
      </div>
    </section>
  )
}

function NarrationContent({ subject }: { subject: Cast }) {
  return (
    <div key={subject.id} className="relative">
      <MutuPrompt
        verb="describe"
        subject={
          <EncryptedText
            text={subject.name}
            encryptedClassName="text-label-dim"
            revealedClassName="text-white"
            revealDelayMs={22}
            flipDelayMs={28}
          />
        }
      />

      <h3 className="mt-3 font-medium font-mono text-[clamp(0.9rem,1.2vw,1.1rem)] text-foreground leading-[1.55] tracking-[-0.005em]">
        <EncryptedText
          text={subject.mutuLine}
          encryptedClassName="text-label-dim"
          revealedClassName="text-foreground"
          revealDelayMs={12}
          flipDelayMs={28}
          startDelayMs={120}
        />
      </h3>

      <p className="mt-5 font-mono text-[10px] text-label uppercase tracking-[0.4em]">
        <span className="text-label-dim">{"//"}</span>{" "}
        <EncryptedText
          text={subject.title}
          encryptedClassName="text-label-dim"
          revealedClassName="text-label"
          revealDelayMs={16}
          flipDelayMs={28}
          startDelayMs={300}
        />
      </p>
    </div>
  )
}

function Carousel({
  active,
  onChange,
  subject,
}: {
  active: number
  onChange: (i: number) => void
  subject: Cast
}) {
  const total = CAST.length

  return (
    <div className="w-full max-w-md">
      <div className="relative mx-auto flex aspect-square max-w-72 items-center justify-center sm:max-w-104">
        <div
          className="pointer-events-none absolute inset-0 rounded-full border border-white/12"
          style={{ borderStyle: "dashed" }}
          aria-hidden="true"
        />
        <ProgressRing active={active} />
        <Rocket active={active} />
        <div
          className="pointer-events-none absolute inset-6 rounded-full border border-white/8"
          aria-hidden="true"
        />

        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-1/2 size-48 -translate-x-1/2 -translate-y-1/2 rounded-full sm:size-72"
          style={{
            background:
              "radial-gradient(circle, rgb(var(--brand-rgb) / 0.35) 0%, rgb(var(--brand-rgb) / 0.12) 45%, rgb(var(--brand-rgb) / 0) 75%)",
            filter: "blur(8px)",
          }}
        />
        <div className="relative z-10 size-36 overflow-hidden rounded-full border border-brand/45 shadow-[0_0_50px_rgb(var(--brand-rgb)/0.5),inset_0_0_30px_rgb(var(--brand-rgb)/0.1)] sm:size-52">
          <Portrait subject={subject} variant="stage" />
          <CornerTicks />
        </div>

        {CAST.map((c, i) => {
          const angle = (i / total) * Math.PI * 2 - Math.PI / 2
          const xPct = Math.cos(angle) * 50
          const yPct = Math.sin(angle) * 50
          const isActive = i === active
          return (
            <button
              key={c.id}
              type="button"
              aria-label={`Subject ${c.id}: ${c.name}`}
              onClick={() => onChange(i)}
              style={{
                left: `calc(50% + ${xPct}%)`,
                top: `calc(50% + ${yPct}%)`,
                transform: `translate(-50%, -50%) scale(${isActive ? 1.1 : 1})`,
              }}
              className={cn(
                "absolute size-9 cursor-pointer overflow-hidden rounded-full border transition-all duration-500 sm:size-11",
                isActive
                  ? "border-brand shadow-[0_0_18px_rgb(var(--brand-rgb)/0.4)]"
                  : "border-white/25 hover:border-white/55"
              )}
            >
              <Portrait subject={c} variant="thumb" />
            </button>
          )
        })}
      </div>

      <CastInfo subject={subject} />
    </div>
  )
}

function ProgressRing({ active }: { active: number }) {
  const ringRef = useRef<SVGCircleElement>(null)
  const R = 49.5
  const C = 2 * Math.PI * R

  // biome-ignore lint/correctness/useExhaustiveDependencies: active retriggers tween on subject change
  useEffect(() => {
    if (!ringRef.current) return
    const tween = gsap.fromTo(
      ringRef.current,
      { strokeDashoffset: C },
      {
        strokeDashoffset: 0,
        duration: ROTATE_INTERVAL_MS / 1000,
        ease: "none",
      }
    )
    return () => {
      tween.kill()
    }
  }, [active, C])

  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 size-full -rotate-90"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      <circle
        ref={ringRef}
        cx="50"
        cy="50"
        r={R}
        fill="none"
        stroke="var(--color-brand)"
        strokeWidth="0.4"
        strokeLinecap="round"
        strokeDasharray={C}
        strokeDashoffset={C}
        style={{ filter: "drop-shadow(0 0 4px rgb(var(--brand-rgb) / 0.45))" }}
      />
    </svg>
  )
}

function Rocket({ active }: { active: number }) {
  const wrapRef = useRef<HTMLDivElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: active retriggers tween on subject change
  useEffect(() => {
    if (!wrapRef.current) return
    const tween = gsap.fromTo(
      wrapRef.current,
      { rotate: 0 },
      {
        rotate: 360,
        duration: ROTATE_INTERVAL_MS / 1000,
        ease: "none",
      }
    )
    return () => {
      tween.kill()
    }
  }, [active])

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
    >
      <div
        className="absolute"
        style={{
          left: "50%",
          top: 0,
          width: "26px",
          height: "26px",
          transform: "translate(-50%, -50%) rotate(90deg)",
          backgroundColor: "var(--color-brand)",
          maskImage: "url(/rocket.svg)",
          WebkitMaskImage: "url(/rocket.svg)",
          maskSize: "contain",
          WebkitMaskSize: "contain",
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
          maskPosition: "center",
          WebkitMaskPosition: "center",
          filter: "drop-shadow(0 0 8px rgb(var(--brand-rgb) / 0.85))",
        }}
      />
    </div>
  )
}

function CastInfo({ subject }: { subject: Cast }) {
  return (
    <SubjectCaption
      title={subject.title}
      name={subject.name}
      withTopBorder
      animateKey={subject.id}
      className="mt-10"
    />
  )
}

function Portrait({
  subject,
  variant,
}: {
  subject: Cast
  variant: "stage" | "thumb"
}) {
  const isStage = variant === "stage"
  return (
    <div className={cn("absolute inset-0 bg-linear-to-br", subject.hue)}>
      <img
        src={subject.image}
        alt={subject.name}
        width={900}
        height={1200}
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
        decoding="async"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-25 mix-blend-overlay"
        style={{
          backgroundImage:
            "repeating-linear-gradient(180deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 4px)",
        }}
      />
      {isStage && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-linear-to-t from-black/55 via-transparent to-black/15"
          />
          <span className="absolute right-3 bottom-3 font-mono text-[9px] text-label uppercase tracking-[0.3em]">
            {subject.id}
          </span>
        </>
      )}
    </div>
  )
}
